import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { Modal, Button, Select } from '../common';
import DatePicker from '../common/DatePicker';
import { toast } from 'react-hot-toast';
import { ShiftType } from '../../types/schedule';
import { shiftService } from '../../lib/api/services/shiftService';

// Define schema for shift creation
const createShiftSchema = z.object({
  date: z.date({ required_error: 'Date is required' }),
  shift_type: z.enum(['day', 'night'], { required_error: 'Shift type is required' }),
  provider_id: z.string().optional(),
});

type CreateShiftFormValues = z.infer<typeof createShiftSchema>;

interface CreateShiftModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleId: string;
  initialDate?: Date;
  providers: Array<{ user_id: string; full_name?: string; shift_type: string }>;
  onShiftCreated?: () => void;
}

const CreateShiftModal: React.FC<CreateShiftModalProps> = ({
  isOpen,
  onClose,
  scheduleId,
  initialDate,
  providers,
  onShiftCreated,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { control, handleSubmit, watch, formState: { errors }, reset } = useForm<CreateShiftFormValues>({
    resolver: zodResolver(createShiftSchema),
    defaultValues: {
      date: initialDate || new Date(),
      shift_type: 'day',
      provider_id: '',
    }
  });
  
  const selectedShiftType = watch('shift_type') as ShiftType;
  
  // Filter providers based on shift type
  const eligibleProviders = providers.filter(provider => {
    if (selectedShiftType === 'day') {
      return provider.shift_type === 'days_only' || provider.shift_type === 'both';
    } else {
      return provider.shift_type === 'nights_only' || provider.shift_type === 'both';
    }
  });
  
  const providerOptions = [
    { value: '', label: 'Unassigned' },
    ...eligibleProviders.map(provider => ({
      value: provider.user_id,
      label: provider.full_name || provider.user_id,
    })),
  ];
  
  const onSubmit = async (data: CreateShiftFormValues) => {
    setIsSubmitting(true);
    
    try {
      // Prepare shift data for API
      const shiftData = {
        schedule_id: scheduleId,
        date: format(data.date, 'yyyy-MM-dd'),
        shift_type: data.shift_type,
        provider_id: data.provider_id === '' ? null : data.provider_id,
      };
      
      // Use shiftService instead of fetch API
      const { error } = await shiftService.createShift(shiftData);
      
      if (error) {
        throw new Error('Failed to create shift');
      }
      
      toast.success('Shift created successfully');
      reset();
      onClose();
      
      if (onShiftCreated) {
        onShiftCreated();
      }
    } catch (error) {
      console.error('Error creating shift:', error);
      toast.error('Failed to create shift. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Shift">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-4 space-y-4">
          <div>
            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Shift Date"
                  selected={field.value}
                  onChange={field.onChange}
                  error={errors.date?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </div>
          
          <div>
            <Controller
              name="shift_type"
              control={control}
              render={({ field }) => (
                <Select
                  label="Shift Type"
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  error={errors.shift_type?.message}
                  disabled={isSubmitting}
                  options={[
                    { value: 'day', label: 'Day Shift' },
                    { value: 'night', label: 'Night Shift' },
                  ]}
                />
              )}
            />
          </div>
          
          <div>
            <Controller
              name="provider_id"
              control={control}
              render={({ field }) => (
                <Select
                  label="Assign Provider (Optional)"
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  error={errors.provider_id?.message}
                  disabled={isSubmitting || eligibleProviders.length === 0}
                  options={providerOptions}
                  helperText={
                    eligibleProviders.length === 0
                      ? "No eligible providers for this shift type"
                      : "Select a provider to assign or leave unassigned"
                  }
                />
              )}
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
          >
            Create Shift
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateShiftModal; 